import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Calendar, ChevronDown, ChevronUp, LucideAngularModule, MapPin, Video } from 'lucide-angular';
import type { LinksItemModel } from '../../../server/models/links-item-model';
import {
  type EventItem,
  type EventType,
  type EventWithLinkItems,
  isEventEventWithDescriptionMarkDownField,
  isEventWithLinks,
} from '../../core/models/middleware/event-item.model';
import type { OtherEventItemModel } from '../../core/models/other-event-item.model';
import { EventService } from '../../core/services/event.service';
import { MicrosoftTeamsIntegrationService } from '../../core/services/microsoft-teams-integration.service';
import { getEventCategoryColor, getEventCategoryLabel } from '../../core/utils/event-category.util';
import { BadgeComponent } from '../../shared/components/badge/badge.component';
import { DatetimeInfoLineComponent } from '../../shared/components/datetime-info-line/datetime-info-line.component';
import { LinkBoxComponent } from '../../shared/components/link-box/link-box.component';
import { ProgressiveImageComponent } from '../../shared/components/progressive-image/progressive-image.component';
import { SharedCardComponent } from '../../shared/components/shared-card/shared-card.component';
import { EnforceNewLinePipe } from '../../shared/pipes/enforce-new-line.pipe';
import { UpdatesComponent } from '../updates/updates.component';

@Component({
  selector: 'app-other-overview',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    SharedCardComponent,
    ProgressiveImageComponent,
    UpdatesComponent,
    LinkBoxComponent,
    DatetimeInfoLineComponent,
    BadgeComponent,
    EnforceNewLinePipe,
  ],
  templateUrl: './other-overview.component.html',
})
export class OtherOverviewComponent {
  // Icons
  readonly events = Calendar;
  readonly mapPin = MapPin;
  readonly chevronDown = ChevronDown;
  readonly chevronUp = ChevronUp;

  readonly eventType: EventType = 'Other';

  private route = inject(ActivatedRoute);
  private eventService = inject(EventService);
  private microsoftTeamsIntegrationService = inject(MicrosoftTeamsIntegrationService);

  private now = signal(new Date());

  event = computed(() => this.eventService.getCurrentEvent() as OtherEventItemModel);
  eventCategory = computed(() => this.event().category);

  // Event data
  eventDocumentId = this.route.snapshot.params.eventDocumentId as string;

  hasUpdates = false;

  eventLocation = computed(() => {
    const currentEvent = this.event();
    if (currentEvent && 'city' in currentEvent && 'country' in currentEvent) {
      return `${currentEvent.city}, ${currentEvent.country}`;
    }
    return '';
  });

  eventStartDate = computed(() => {
    const currentEvent = this.event();
    return currentEvent?.startDate;
  });
  eventEndDate = computed(() => {
    const currentEvent = this.event();
    return currentEvent?.endDate;
  });

  eventDateCompact = computed(() => {
    const startDate = this.eventStartDate();
    const endDate = this.eventEndDate();
    if (!startDate) return '';

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;

    // Format start date compactly with weekday
    const startWeekday = start.toLocaleDateString('de-DE', { weekday: 'short' });
    const startDay = start.getDate().toString().padStart(2, '0');
    const startMonth = (start.getMonth() + 1).toString().padStart(2, '0');
    let result = `${startWeekday}, ${startDay}.${startMonth}`;

    // If multi-day, add end date with year and weekday
    if (end && start.toDateString() !== end.toDateString()) {
      const endWeekday = end.toLocaleDateString('de-DE', { weekday: 'short' });
      const endDay = end.getDate().toString().padStart(2, '0');
      const endMonth = (end.getMonth() + 1).toString().padStart(2, '0');
      const endYear = end.getFullYear().toString().slice(-2);
      result += ` - ${endWeekday}, ${endDay}.${endMonth}.${endYear}`;
    }

    return result;
  });

  description = computed(() => {
    const currentEvent = this.event();
    if (currentEvent && isEventEventWithDescriptionMarkDownField(currentEvent)) {
      return currentEvent.description;
    }
    return undefined;
  });

  // Description expansion state
  isDescriptionExpanded = signal(false);

  // Toggle description expansion
  toggleDescription() {
    this.isDescriptionExpanded.update((current) => !current);
  }

  // Computed property for description display
  displayDescription = computed(() => {
    const description = this.description() || '';
    const maxLength = 150;

    if (this.isDescriptionExpanded() || description.length <= maxLength) {
      return description;
    }

    return `${description.substring(0, maxLength)}...`;
  });

  // Check if description needs expansion
  needsExpansion = computed(() => {
    const description = this.event()?.description || '';
    return description.length > 150;
  });

  moodImageData = computed(() => {
    const currentEvent = this.event();
    if (currentEvent?.moodImage) {
      return {
        url: currentEvent.moodImage.url,
        thumbnailUrl: currentEvent.moodImage.thumbnailUrl,
        width: currentEvent.moodImage.width,
        height: currentEvent.moodImage.height,
        alt: `${currentEvent.title} mood image`,
      };
    }
    return null;
  });

  joinButtonActivated = computed(() => {
    if (!this.event()) {
      return false;
    }

    const now = this.now().getTime();
    const gracePeriod = 15 * 60 * 1000;
    const start = this.event()!.startDate.getTime() - gracePeriod;
    const end = this.event()!.endDate.getTime() + gracePeriod;

    return now >= start && now <= end;
  });

  constructor() {
    if (this.eventService.getCurrentEvent()?.documentId === this.eventDocumentId) {
      // Display stored event until the refresh is doneEventWithAgendaItems
      this.eventService.setCurrentEvent(this.eventService.getCurrentEvent() as OtherEventItemModel);
    } else {
      this.eventService.clearCurrentEvent();
    }

    this.eventService.getEvent(this.eventDocumentId, this.eventType).subscribe({
      next: (data) => {
        if (data) {
          this.eventService.setCurrentEvent(data as OtherEventItemModel);
        } else {
          // If we don't receive event data, we need to clear old data, to prevent us from displaying the wrong data
          this.eventService.clearCurrentEvent();
        }
      },
      error: (error) => {
        this.eventService.clearCurrentEvent();
        // TODO [#215]: use winston for logging
        console.error(`Error loading ${this.eventType} event:`, error);
        throw error;
      },
      complete: () => {
        if (
          !this.eventService.getCurrentEvent() ||
          this.eventDocumentId !== this.eventService.getCurrentEvent()?.documentId
        ) {
          this.eventService.clearCurrentEvent();
          throw Error(`Event with id ${this.eventDocumentId} not found`);
        }
      },
    });

    // Refresh current time for signals every 5 seconds
    effect(() => {
      const intervalId = setInterval(() => {
        this.now.set(new Date());
      }, 5000);
      return () => clearInterval(intervalId);
    });
  }

  public eventHasLinks(event: EventItem<EventType>): event is EventWithLinkItems<EventType> {
    return !!event && isEventWithLinks(event) && !!event.links && event.links.length > 0;
  }

  public getEventLinks(event: EventWithLinkItems<EventType>): LinksItemModel[] | undefined {
    return event.links;
  }

  joinMeeting() {
    if (!!this.event()?.teamsMeetingUrl && this.microsoftTeamsIntegrationService.isMicrosoftTeamsEnvironment()) {
      this.microsoftTeamsIntegrationService.joinMeeting(this.event()!.teamsMeetingUrl!);
    } else {
      window.open(this.event()!.teamsMeetingUrl, '_blank');
    }
  }

  protected readonly getEventCategoryLabel = getEventCategoryLabel;
  protected readonly video = Video;
  protected readonly getEventCategoryColor = getEventCategoryColor;
}
